import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import StoryblokClient from 'storyblok-js-client';
import API from '../../services/API';
import Utils from '../../services/utils';

const assetClassFilter = window.ttCentre.assetClass || '';
const assetClassesFilters = [
  {
    name: 'Equities',
    checked: false,
    id: 'equities',
  },
  {
    name: 'Commodities',
    checked: false,
    id: 'commodities',
  },
  {
    name: 'Currencies',
    checked: false,
    id: 'currencies',
  },
  {
    name: 'Fixed Income',
    checked: false,
    id: 'fixedIncome',
  },
  {
    name: 'Daily Market Update',
    checked: false,
    id: 'dailyMarketUpdate',
  },
  {
    name: 'Tactical Update',
    checked: false,
    id: 'tacticalUpdate',
  },
];

// Dev token also shows drafted changes from story blok
const STORYBLOK_DEV_TOKEN = '9XW7lqRYZqQtasQEcNk3Wgtt';

// Prod token only shows published changes from story blok
const STORYBLOK_PROD_TOKEN = 'iRzZGkOMouUHxqynd2hu6gtt';

// --- Storyblok client for posts ---
const storyblokApi = new StoryblokClient({
  accessToken:
    process.env.REACT_APP_NODE_ENV === 'development'
      ? STORYBLOK_DEV_TOKEN
      : STORYBLOK_PROD_TOKEN, // TODO: inject via env var
  cache: { clear: 'auto', type: 'memory' },
});

const initialState = {
  stories: [],
  data: [],
  blink: [],
  filters: assetClassesFilters.map((f) => ({
    name: f.name,
    checked: f.checked,
    id: f.id,
  })),
  filteredPosts: [],
  sliderHeight: 800,
  hasDefaultFilter: assetClassFilter || false,
  modals: {},
  loading: false,
  error: null,
  page: 1,
  selectedContributor: 'all',
};
const siteCoreLang = (window.ttCentre && window.ttCentre.isoCode) || 'en-GB';

const language = {
  'en-gb': 'en-gb',
  'de-de': 'de-de',
  'en-us': 'en-us',
  'it-it': 'it',
  'fr-fr': 'fr-fr',
};

// --- Async thunk: fetch stories with pagination and contributor filter ---
export const fetchStories = createAsyncThunk(
  'stories/fetch',
  async ({ page = 1 }, { rejectWithValue }) => {
    try {
      const { data } = await storyblokApi.get('cdn/stories', {
        version:
          process.env.REACT_APP_NODE_ENV === 'development'
            ? 'draft'
            : 'published',
        language: language[siteCoreLang.toLowerCase()] || '',
        fallback_lang: 'default',
        per_page: 100,
      });
      return { stories: data.stories, page };
    } catch (err) {
      // Normalize error
      const message =
        (err && (err.message || err.toString())) || 'Unknown error';
      return rejectWithValue(message);
    }
  },
);

export const getAssetClasses = createAsyncThunk('assets/retrieve', async () => {
  const res = await API.getAssetClasses();
  return res.map((r) => ({
    name: r.name,
    groups: r.groups.map((g) => ({
      name: g.name,
      underlyingList: Utils.asignColorsToItems(g.underlyingList),
    })),
  }));
});

const appSlice = createSlice({
  name: 'assetClasses',
  initialState,
  reducers: {
    setSelectedContributor: {
      reducer(state, action) {
        state.selectedContributor = action.payload || 'all';
      },
    },
    setPage: {
      reducer(state, action) {
        state.page = Number(action.payload) || 1;
      },
    },
    togglePostModal: {
      reducer(state, action) {
        state.modals[action.payload] = !state.modals[action.payload];
      },
    },
    setStories: {
      reducer(state, action) {
        const sortedPosts = action.payload
          .filter((post) => post.postTitle !== '')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        let mostRecentDailyUpdate = null;
        let mostRecentTacticalUpdate = null;

        let resultPosts = [];

        sortedPosts.forEach((post) => {
          const hasDailyUpdate = post.assetClasses.includes(
            'Daily Market Update',
          );
          const hasTacticalUpdate =
            post.assetClasses.includes('Tactical Update');

          if (hasDailyUpdate && !mostRecentDailyUpdate) {
            mostRecentDailyUpdate = post;
          } else if (hasTacticalUpdate && !mostRecentTacticalUpdate) {
            mostRecentTacticalUpdate = post;
          } else if (!hasDailyUpdate && !hasTacticalUpdate) {
            resultPosts.push(post);
          }
        });

        if (mostRecentTacticalUpdate) {
          resultPosts.unshift(mostRecentTacticalUpdate);
        }
        if (mostRecentDailyUpdate) {
          resultPosts.unshift(mostRecentDailyUpdate);
        }
        // Apply filter based on assetClassFilter if it exists
        if (assetClassFilter) {
          resultPosts = resultPosts.filter((post) =>
            post.assetClasses.some(
              (p) => p.toLowerCase() === assetClassFilter.toLowerCase(),
            ),
          );
        }

        // Set the final result to the state
        state.stories = resultPosts;
      },
    },
    setSelectedFilters: {
      reducer(state, action) {
        state.filters = state.filters.map((filter) => {
          if (filter.id === action.payload) {
            filter.checked = !filter.checked;
          }
          return filter;
        });
        state.filteredPosts = state.stories.filter((blog) =>
          current(state).filters.some(
            (filter) =>
              blog.assetClasses &&
              blog.assetClasses.some(
                (assetClass) => assetClass === filter.name,
              ) &&
              filter.checked,
          ),
        );
      },
    },
    setFilteredPosts: {
      reducer(state, action) {
        if (state.hasDefaultFilter) {
          state.filteredPosts = action.payload.filter((blog) =>
            current(state).filters.some(
              (filter) =>
                blog.assetClasses &&
                blog.assetClasses.some(
                  (assetClass) => assetClass === filter.name,
                ) &&
                filter.checked,
            ),
          );
        } else {
          state.filteredPosts = action.payload.filter((blog) =>
            current(state).filters.some(
              (filter) =>
                blog.assetClasses &&
                blog.assetClasses.some(
                  (assetClass) => assetClass === filter.name,
                ) &&
                filter.checked,
            ),
          );
        }
      },
    },
    setSliderHeight: {
      reducer(state, action) {
        state.sliderHeight = action.payload;
      },
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStories.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;

        // --- Apply the same business rules as setStories ---
        const incoming = action.payload.stories || [];
        const sortedPosts = incoming
          .filter((post) => post.postTitle !== '')
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        let mostRecentDailyUpdate = null;
        let mostRecentTacticalUpdate = null;
        let resultPosts = [];

        sortedPosts.forEach((post) => {
          const hasDailyUpdate = post.assetClasses?.includes(
            'Daily Market Update',
          );
          const hasTacticalUpdate =
            post.assetClasses?.includes('Tactical Update');

          if (hasDailyUpdate && !mostRecentDailyUpdate) {
            mostRecentDailyUpdate = post;
          } else if (hasTacticalUpdate && !mostRecentTacticalUpdate) {
            mostRecentTacticalUpdate = post;
          } else if (!hasDailyUpdate && !hasTacticalUpdate) {
            resultPosts.push(post);
          }
        });

        if (mostRecentTacticalUpdate)
          resultPosts.unshift(mostRecentTacticalUpdate);
        if (mostRecentDailyUpdate) resultPosts.unshift(mostRecentDailyUpdate);

        // Optional deep-link filter via global flag
        if (assetClassFilter) {
          resultPosts = resultPosts.filter((post) =>
            post.assetClasses?.some(
              (p) => p.toLowerCase() === assetClassFilter.toLowerCase(),
            ),
          );
        }

        state.stories = resultPosts;

        // Recompute filteredPosts using current filters
        state.filteredPosts = state.stories.filter((blog) =>
          current(state).filters.some(
            (filter) =>
              blog.assetClasses &&
              blog.assetClasses.some(
                (assetClass) => assetClass === filter.name,
              ) &&
              filter.checked,
          ),
        );
      })
      .addCase(fetchStories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || action.error?.message || 'Failed to fetch stories';
      })
      .addCase(getAssetClasses.fulfilled, (state, action) => {
        state.blink = Utils.getChangedTickers(state.data, action.payload);
        state.data = action.payload;
      });
  },
});

const { reducer } = appSlice;
export const {
  setStories,
  setSelectedFilters,
  setFilteredPosts,
  setSliderHeight,
  togglePostModal,
  setSelectedContributor,
  setPage,
} = appSlice.actions;

export default reducer;
